/**
 * Month Flow — import / export.
 *
 * The import target is deliberately forgiving. The household bill list lives in
 * a spreadsheet whose schedule column is written for a human ("1st", "Every
 * Friday", "Monthly"), and demanding a machine format would mean retyping 21
 * rows by hand. So `parseScheduleText` reads what people actually write, and
 * anything it can't read becomes a visible row-level error in the preview
 * rather than a silently-dropped bill.
 */

import Papa from 'papaparse';
import {
  RecurrenceKind,
  RecurringMoneyItem,
  RhythmOccurrence,
  TxScope,
} from '../types';
import { DAY_NAMES, MONTH_NAMES, describeSchedule, isDayString } from './rhythm';
import { getCategoryLabel } from './categories';

// ---------------------------------------------------------------------------
// Schedule text parsing
// ---------------------------------------------------------------------------

/** The schedule fields a parsed string can fill in. */
export interface ParsedSchedule {
  kind: RecurrenceKind;
  dayOfMonth?: number;
  weekday?: number;
  month?: number;
  anchorDate?: string;
}

const WEEKDAY_LOOKUP: Record<string, number> = {
  sun: 0, sunday: 0,
  mon: 1, monday: 1,
  tue: 2, tues: 2, tuesday: 2,
  wed: 3, weds: 3, wednesday: 3,
  thu: 4, thur: 4, thurs: 4, thursday: 4,
  fri: 5, friday: 5,
  sat: 6, saturday: 6,
};

const MONTH_LOOKUP: Record<string, number> = MONTH_NAMES.reduce((acc, name, i) => {
  acc[name.toLowerCase()] = i + 1;
  acc[name.slice(0, 3).toLowerCase()] = i + 1;
  return acc;
}, {} as Record<string, number>);

/**
 * Read a human schedule string into a recurrence.
 *
 * Handles, in priority order:
 *   '2026-08-15' / '15/08/2026'   → one-off on that date
 *   'every friday' / 'fridays'    → weekly
 *   'fortnightly' / 'every 2 wks' → fortnightly
 *   'quarterly'                   → quarterly
 *   '15 March' / 'yearly 15 Mar'  → yearly
 *   'yearly' / 'annually'         → yearly (day unknown → 1 Jan, user edits)
 *   '1st' / '23rd' / 'day 7' / '7'→ monthly on that day
 *   'monthly' (no day)            → monthly, spread across the month
 *
 * Returns null when nothing matched, so the caller can show the raw text back
 * to the user instead of inventing a schedule.
 */
export function parseScheduleText(raw: string): ParsedSchedule | null {
  const text = String(raw || '').trim().toLowerCase();
  if (!text) return null;

  // --- explicit dates -------------------------------------------------------
  if (isDayString(text)) return { kind: 'one_off', anchorDate: text };

  const dmy = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/.exec(text);
  if (dmy) {
    const d = Number(dmy[1]);
    const m = Number(dmy[2]);
    let y = Number(dmy[3]);
    if (y < 100) y += 2000;
    const candidate = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    if (isDayString(candidate)) return { kind: 'one_off', anchorDate: candidate };
  }

  // --- weekly ---------------------------------------------------------------
  // Checked before the day-of-month branch: 'every 1st friday' should not be
  // read as "the 1st of the month".
  const weekdayMatch = /\b(sun|mon|tue|tues|wed|weds|thu|thur|thurs|fri|sat|sunday|monday|tuesday|wednesday|thursday|friday|saturday)s?\b/.exec(text);
  if (weekdayMatch && !/\b(month|year|quarter)/.test(text)) {
    const weekday = WEEKDAY_LOOKUP[weekdayMatch[1]];
    if (weekday !== undefined) {
      if (/fortnight|every\s*(2|two)\s*(week|wk)/.test(text)) {
        return { kind: 'fortnightly', weekday };
      }
      return { kind: 'weekly', weekday };
    }
  }

  if (/^\s*(weekly|every\s*week)\s*$/.test(text)) return { kind: 'weekly', weekday: 1 };
  if (/fortnight|every\s*(2|two)\s*(week|wk)/.test(text)) return { kind: 'fortnightly' };
  if (/quarter/.test(text)) return { kind: 'quarterly' };

  // --- yearly ---------------------------------------------------------------
  const monthMatch = /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\b/.exec(text);
  if (monthMatch) {
    const month = MONTH_LOOKUP[monthMatch[1]];
    const dayMatch = /\b(\d{1,2})(?:st|nd|rd|th)?\b/.exec(text);
    if (month) {
      return { kind: 'yearly', month, dayOfMonth: dayMatch ? Number(dayMatch[1]) : 1 };
    }
  }
  if (/year|annual/.test(text)) {
    const dayMatch = /\b(\d{1,2})(?:st|nd|rd|th)?\b/.exec(text);
    return { kind: 'yearly', month: 1, dayOfMonth: dayMatch ? Number(dayMatch[1]) : 1 };
  }

  // --- monthly on a day -----------------------------------------------------
  const domMatch = /\b(\d{1,2})(?:st|nd|rd|th)\b/.exec(text) || /^\s*(?:day\s*)?(\d{1,2})\s*$/.exec(text);
  if (domMatch) {
    const dom = Number(domMatch[1]);
    if (dom >= 1 && dom <= 31) return { kind: 'monthly_day', dayOfMonth: dom };
  }

  // --- monthly, no day ------------------------------------------------------
  // 'Monthly' with nothing else is exactly the groceries/fuel case: a budget
  // for the month rather than a dated bill. Spreading is what keeps the
  // balance curve honest.
  if (/month/.test(text)) return { kind: 'monthly_spread' };

  return null;
}

/** Parse '$1,234.56', '1234.56', '(50.00)' → 1234.56 / 50. Returns null on
 *  anything that isn't a number, so a header row can't become a $0 bill. */
export function parseAmount(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? Math.abs(raw) : null;
  const text = String(raw ?? '').trim();
  if (!text) return null;
  const negative = /^\(.*\)$/.test(text);
  const cleaned = text.replace(/[()$,\s]/g, '').replace(/^-/, '');
  if (!/^\d*\.?\d+$/.test(cleaned)) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  return negative ? Math.abs(n) : Math.abs(n);
}

// ---------------------------------------------------------------------------
// Category guessing
// ---------------------------------------------------------------------------

/** Name → category guesses, applied in order. First match wins, so the more
 *  specific patterns come first — 'Coles CC' is a card repayment, not a grocery
 *  shop, so the card rule has to be tested before the supermarket one.
 *
 *  Every stem that can inflect ends in `\w*` rather than `\b`. A trailing `\b`
 *  after 'electric' cannot match 'Electricity' — the boundary needs a non-word
 *  character and 'i' is a word character — which silently dropped four of these
 *  rules until the tests caught it.
 *
 *  Only a starting point: the preview lets the user change any row before a
 *  single document is written. */
const CATEGORY_GUESSES: Array<[RegExp, string]> = [
  [/\b(?:visa|mastercard|amex|credit card|cc|loan|repayment|afterpay|zip|humm|gem)\b/i, 'expense:debt_repayment'],
  [/\b(?:council|rates?|strata|body corp|mortgage|rent)\b/i, 'expense:home'],
  [/\b(?:school|kindy|childcare|tuition|uni)\w*\b/i, 'expense:education'],
  [/\b(?:ato|gst|bas|payg|tax)\w*\b/i, 'expense:tax'],
  [/\b(?:electric\w*|origin|agl|energy|power|water|gas)\b/i, 'expense:utilities'],
  [/\b(?:optus|telstra|vodafone|phone|mobile|sim)\w*\b/i, 'expense:phone'],
  [/\b(?:starlink|nbn|internet|broadband|siteground|hosting|domain)\w*\b/i, 'expense:internet'],
  [/\b(?:google cloud|aws|azure|github|adobe|microsoft|anthropic|openai|figma)\b/i, 'expense:software'],
  [/\b(?:netflix|disney|spotify|youtube|audible|stan|binge|kayo|prime|google one|icloud)\b/i, 'expense:subscriptions'],
  [/\b(?:coffee|cafe|dining|takeaway|uber eats|doordash|menulog)\b/i, 'expense:dining'],
  [/\b(?:grocer\w*|coles|woolworths|aldi|drakes|foodland|shopping)\b/i, 'expense:groceries'],
  [/\b(?:fuel|petrol|bp|caltex|shell|ampol|united)\b/i, 'expense:fuel'],
  [/\b(?:ezyreg|rego|registration|car|vehicle)\b/i, 'expense:vehicle'],
  [/\b(?:insur\w*|rac|aami|allianz|nrma|medibank|bupa)\b/i, 'expense:insurance'],
  [/\b(?:ads|adwords|advertis\w*|marketing|seo)\b/i, 'expense:advertising'],
  [/\b(?:bank fee|account fee|monthly fee)s?\b/i, 'expense:bank_fees'],
];

export function guessCategory(name: string, direction: 'in' | 'out'): string {
  if (direction === 'in') return 'income:other';
  for (const [re, category] of CATEGORY_GUESSES) {
    if (re.test(name)) return category;
  }
  return 'expense:other';
}

// ---------------------------------------------------------------------------
// CSV → draft items
// ---------------------------------------------------------------------------

/** A parsed row awaiting the user's confirmation. Everything is editable in
 *  the preview table before a single document is written. */
export interface DraftItem {
  /** Row number in the source file, for the error list. */
  line: number;
  name: string;
  amount: number;
  expectedAmount?: number;
  schedule: ParsedSchedule;
  /** The raw schedule text, kept so the preview can show what we read. */
  scheduleRaw: string;
  direction: 'in' | 'out';
  category: string;
  scope: TxScope;
  /** True when an existing item already has this name (case-insensitive). */
  duplicateOf?: string;
  /** Set when the schedule column couldn't be read; the row defaults to
   *  monthly-spread and is flagged in the preview. */
  scheduleUnreadable?: boolean;
}

export interface ParseCsvResult {
  drafts: DraftItem[];
  errors: Array<{ line: number; text: string; reason: string }>;
  /** Header names as found, so the mapping UI can show them. */
  headers: string[];
}

/** Column-name synonyms. Matching is case- and punctuation-insensitive, which
 *  is what lets the spreadsheet's 'Bill:' / 'Amount:' / 'Potentially:' headers
 *  work with no mapping step at all. */
const HEADER_SYNONYMS: Record<keyof ColumnMap, string[]> = {
  name: ['bill', 'name', 'item', 'description', 'expense', 'payee', 'what'],
  amount: ['amount', 'cost', 'value', 'price', 'full', 'max'],
  expectedAmount: ['potentially', 'expected', 'likely', 'realistic', 'actual', 'min', 'typical'],
  schedule: ['date', 'when', 'schedule', 'frequency', 'due', 'day', 'repeats'],
  category: ['category', 'type', 'bucket'],
  direction: ['direction', 'in/out', 'inout', 'flow'],
  scope: ['scope', 'books', 'business/family'],
  notes: ['notes', 'note', 'comment', 'comments'],
};

export interface ColumnMap {
  name: string | null;
  amount: string | null;
  expectedAmount: string | null;
  schedule: string | null;
  category: string | null;
  direction: string | null;
  scope: string | null;
  notes: string | null;
}

function normaliseHeader(h: string): string {
  return String(h || '').toLowerCase().replace(/[^a-z0-9/]/g, '');
}

/** Best-guess mapping from the file's headers onto our fields. */
export function autoMapColumns(headers: string[]): ColumnMap {
  const map: ColumnMap = {
    name: null, amount: null, expectedAmount: null, schedule: null,
    category: null, direction: null, scope: null, notes: null,
  };
  const normalised = headers.map((h) => ({ raw: h, norm: normaliseHeader(h) }));
  const taken = new Set<string>();

  (Object.keys(HEADER_SYNONYMS) as Array<keyof ColumnMap>).forEach((field) => {
    for (const synonym of HEADER_SYNONYMS[field]) {
      const hit = normalised.find((h) => !taken.has(h.raw) && h.norm === synonym);
      if (hit) {
        map[field] = hit.raw;
        taken.add(hit.raw);
        return;
      }
    }
    // Second pass: substring match, for headers like 'Amount (inc GST)'.
    for (const synonym of HEADER_SYNONYMS[field]) {
      const hit = normalised.find((h) => !taken.has(h.raw) && h.norm.includes(synonym));
      if (hit) {
        map[field] = hit.raw;
        taken.add(hit.raw);
        return;
      }
    }
  });
  return map;
}

/**
 * Parse CSV text into draft items using a column mapping.
 *
 * Blank rows and rows whose name AND amount are both empty are skipped
 * silently (spreadsheets are full of spacer rows — the source file has one
 * between every bill). Rows with a name but no readable amount become errors,
 * because those are the ones a user would otherwise never notice were missing.
 */
export function parseItemsCsv(
  csvText: string,
  map: ColumnMap,
  existingNames: string[] = []
): ParseCsvResult {
  const parsed = Papa.parse<Record<string, string>>(csvText, {
    header: true,
    skipEmptyLines: 'greedy',
    transformHeader: (h) => h.trim(),
  });

  const headers = parsed.meta.fields || [];
  const drafts: DraftItem[] = [];
  const errors: Array<{ line: number; text: string; reason: string }> = [];
  const existing = new Map(existingNames.map((n) => [n.trim().toLowerCase(), n]));

  parsed.data.forEach((row, i) => {
    const line = i + 2; // +1 for the header, +1 for 1-based line numbers
    const rawName = map.name ? String(row[map.name] ?? '').trim() : '';
    const rawAmount = map.amount ? row[map.amount] : '';
    const amount = parseAmount(rawAmount);

    if (!rawName && amount === null) return; // spacer row

    // A totals row — 'Total', 'Sum', or a name-less amount — is not a bill.
    if (!rawName || /^(total|sum|subtotal|grand total)\b/i.test(rawName)) {
      if (amount !== null && rawName) {
        errors.push({ line, text: rawName, reason: 'Looks like a totals row — skipped' });
      }
      return;
    }
    if (amount === null) {
      errors.push({ line, text: rawName, reason: `Couldn't read an amount from "${String(rawAmount ?? '')}"` });
      return;
    }

    const scheduleRaw = map.schedule ? String(row[map.schedule] ?? '').trim() : '';
    const parsedSchedule = parseScheduleText(scheduleRaw);

    const rawDirection = map.direction ? String(row[map.direction] ?? '').trim().toLowerCase() : '';
    const direction: 'in' | 'out' =
      /^(in|income|credit|\+)/.test(rawDirection) ? 'in' : 'out';

    const rawScope = map.scope ? String(row[map.scope] ?? '').trim().toLowerCase() : '';
    const scope: TxScope = /^(bus|company|work)/.test(rawScope) ? 'business' : 'personal';

    const rawCategory = map.category ? String(row[map.category] ?? '').trim() : '';
    const category = rawCategory && rawCategory.includes(':')
      ? rawCategory
      : guessCategory(rawName, direction);

    const expectedRaw = map.expectedAmount ? row[map.expectedAmount] : undefined;
    const expectedAmount = expectedRaw !== undefined ? parseAmount(expectedRaw) : null;

    drafts.push({
      line,
      name: rawName,
      amount,
      // A "Potentially" of 0 is a real instruction — "we don't expect to pay
      // this next month" — so it must survive as 0 and not be dropped as falsy.
      ...(expectedAmount !== null && expectedAmount !== amount ? { expectedAmount } : {}),
      schedule: parsedSchedule || { kind: 'monthly_spread' },
      scheduleRaw,
      scheduleUnreadable: !parsedSchedule,
      direction,
      category,
      scope,
      duplicateOf: existing.get(rawName.trim().toLowerCase()),
    });
  });

  return { drafts, errors, headers };
}

/** Turn a confirmed draft into the document shape, minus the id/timestamps. */
export function draftToItem(draft: DraftItem): Omit<RecurringMoneyItem, 'id' | 'createdAt' | 'updatedAt'> {
  const { kind, dayOfMonth, weekday, month, anchorDate } = draft.schedule;
  return {
    name: draft.name,
    direction: draft.direction,
    amount: draft.amount,
    ...(draft.expectedAmount !== undefined ? { expectedAmount: draft.expectedAmount } : {}),
    kind,
    ...(dayOfMonth !== undefined ? { dayOfMonth } : {}),
    ...(weekday !== undefined ? { weekday } : {}),
    ...(month !== undefined ? { month } : {}),
    ...(anchorDate !== undefined ? { anchorDate } : {}),
    category: draft.category,
    scope: draft.scope,
    active: true,
  };
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

function csvCell(v: unknown): string {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function toCsv(rows: Array<Array<unknown>>): string {
  return rows.map((r) => r.map(csvCell).join(',')).join('\n');
}

/** Export the recurring items themselves — the round-trip format. Re-importing
 *  this file reproduces the same items, which is what makes it safe to edit the
 *  list in a spreadsheet and bring it back. */
export function itemsToCsv(items: RecurringMoneyItem[]): string {
  const rows: Array<Array<unknown>> = [
    ['Name', 'Amount', 'Potentially', 'Date', 'Direction', 'Category', 'Category label', 'Scope', 'Active', 'Start', 'End', 'Notes'],
  ];
  for (const it of items) {
    rows.push([
      it.name,
      it.amount.toFixed(2),
      it.expectedAmount !== undefined ? it.expectedAmount.toFixed(2) : '',
      scheduleToText(it),
      it.direction,
      it.category,
      getCategoryLabel(it.category),
      it.scope,
      it.active ? 'yes' : 'no',
      it.startDate || '',
      it.endDate || '',
      it.notes || '',
    ]);
  }
  return toCsv(rows);
}

/** The schedule written back in the same dialect `parseScheduleText` reads, so
 *  an exported file re-imports without loss. */
export function scheduleToText(item: RecurringMoneyItem): string {
  switch (item.kind) {
    case 'monthly_day': return ordinalPlain(item.dayOfMonth || 1);
    case 'monthly_spread': return 'Monthly';
    case 'weekly': return `Every ${DAY_NAMES[item.weekday ?? 1]}`;
    case 'fortnightly': return item.anchorDate ? `Fortnightly from ${item.anchorDate}` : 'Fortnightly';
    case 'quarterly': return item.anchorDate ? `Quarterly from ${item.anchorDate}` : 'Quarterly';
    case 'yearly': return `${ordinalPlain(item.dayOfMonth || 1)} ${MONTH_NAMES[(item.month || 1) - 1]}`;
    case 'one_off': return item.anchorDate || 'One-off';
    default: return 'Monthly';
  }
}

function ordinalPlain(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/** Export the PROJECTION — every dated movement in the current view. This is
 *  the one to hand an accountant or paste into another model. */
export function occurrencesToCsv(occurrences: RhythmOccurrence[]): string {
  const rows: Array<Array<unknown>> = [
    ['Date', 'Label', 'Direction', 'Amount', 'Signed amount', 'Source', 'Category', 'Category label', 'Scope', 'Certainty', 'Counterparty', 'Detail'],
  ];
  for (const o of occurrences) {
    rows.push([
      o.date,
      o.label,
      o.direction,
      o.amount.toFixed(2),
      (o.direction === 'out' ? -o.amount : o.amount).toFixed(2),
      o.source,
      o.category,
      getCategoryLabel(o.category),
      o.scope,
      o.certainty,
      o.counterparty || '',
      o.detail || '',
    ]);
  }
  return toCsv(rows);
}

/** Export the day-by-day running balance — the chart as numbers. */
export function daysToCsv(
  days: Array<{ date: string; inflow: number; outflow: number; net: number; balance: number }>
): string {
  const rows: Array<Array<unknown>> = [['Date', 'In', 'Out', 'Net', 'Balance']];
  for (const d of days) {
    rows.push([d.date, d.inflow.toFixed(2), d.outflow.toFixed(2), d.net.toFixed(2), d.balance.toFixed(2)]);
  }
  return toCsv(rows);
}

export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Starter data — the household bill list as supplied
// ---------------------------------------------------------------------------

/**
 * The attached household spreadsheet, verbatim, as CSV in the format the
 * importer reads.
 *
 * Offered as a one-click starting point when the list is empty, and it goes
 * through the SAME preview as any other file — nothing is written until the
 * owner has seen every row and pressed import. Amounts and the "Potentially"
 * column are exactly as supplied; only the category guesses are ours.
 */
export const STARTER_BILLS_CSV = [
  'Bill,Amount,Date,Potentially',
  'Council Rates,305.62,1st,305.62',
  'School,700,1st,700',
  'Google Cloud,15,2nd,15',
  'Google One,32.99,2nd,32.99',
  'Ben ATO,26.53,Every Friday,26.53',
  'Coles CC,79,7th,79',
  'Electricity,500,10th,300',
  'Coffee,57.6,11th,57.6',
  'Optus (phone bill),138.47,14th,138.47',
  'Optus (car sim),35,15th,35',
  'Audible,16.45,16th,16.45',
  'Ezyreg,60.1,16th,60.1',
  'Disney,24.99,18th,24.99',
  'Starlink,75,18th,75',
  'YouTube,39.99,21st,39.99',
  'Coles Shopping Subscription,19,23rd,19',
  'Gem Visa,30,27th,30',
  'Origin,240,28th,0',
  'GST,332.17,30th,0',
  'Groceries,1000,Monthly,500',
  'Fuel,300,Monthly,300',
].join('\n');

/** A blank template for someone starting from scratch. */
export const TEMPLATE_CSV = [
  'Name,Amount,Potentially,Date,Direction,Category,Scope,Notes',
  'Rent,2200,2200,1st,out,expense:home,personal,',
  'Salary,4500,4500,Every Thursday,in,income:other,personal,',
  'Insurance,1450,1450,15 March,out,expense:insurance,personal,Annual premium',
].join('\n');

/** Human summary of a draft's schedule, for the preview table. Shares the
 *  wording used by the item list so the preview reads like the result. */
export function describeDraft(draft: DraftItem): string {
  return describeSchedule({
    ...draft.schedule,
    id: '', name: draft.name, direction: draft.direction, amount: draft.amount,
    category: draft.category, scope: draft.scope, active: true,
  } as RecurringMoneyItem);
}
